import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { PrimaryButton } from '../Button/Buttons';
import { DescriptionAndReasoning } from './DescriptionAndReasoning';
import { UserAnswer } from './UserAnswer';

const OPTION_FOCUS_SHADOW =
  '0 1px 3px 0 rgba(0,0,0,0.10), 0 1px 2px 0 rgba(201, 200, 200, 0.06), 0 0 0 2px #4338CA, 0 0 0 4px #FFF';

const Skeleton = ({ className }: { className?: string }) => {
  const animationStyle = useMemo<CSSProperties>(
    () => ({
      animationDelay: `${Math.random() * 1.2}s`,
      animationDuration: `${2.2 + Math.random() * 1.4}s`,
      backgroundPositionX: `${Math.random() * 100}%`
    }),
    []
  );

  return (
    <div
      className={`questionaire-skeleton-loading rounded-full ${className ?? ''}`}
      style={animationStyle}
    />
  );
};

export const QuestionaireComponentSkeleton = ({
  icon
}: {
  icon: React.ReactNode;
}) => (
  <div className="flex gap-4 p-4 rounded-lg border border-gray-100">
    {icon}
    <div className="flex flex-col gap-3 w-full pt-1">
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-1/2" />
      <div className="flex flex-col gap-3 pt-3">
        <Skeleton className="h-2 w-3/4" />
        <Skeleton className="h-2 w-1/2" />
        <Skeleton className="h-2 w-1/3" />
      </div>
    </div>
  </div>
);

type QuestionaireComponentProps = {
  icon: React.ReactNode;
  mainText?: string;
  description?: string;
  descriptionTitle?: string;
  reasoning?: string;
  reasoningTitle?: string;
  isEditable?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  isGrayedOut?: boolean;
  isEditActive?: boolean;
  userAnswer?: string;
  answerOptions?: string[];
  isError?: boolean;
  errorMessage?: string;
  options?: string[];
  onDescriptionUpdate?: (newContent: string) => void;
  onReasoningUpdate?: (newContent: string) => void;
  onUserAnswerUpdate?: (newAnswer: string) => void;
  onOptionSelect?: (option: string) => void;
  onHeightChange?: (height: number) => void;
  onEditStart?: () => void;
  onEditEnd?: () => void;
};

export const QuestionaireComponent = ({
  icon,
  mainText,
  description,
  descriptionTitle,
  reasoning,
  reasoningTitle,
  isError = false,
  errorMessage,
  isEditable = true,
  isDisabled = false,
  isLoading = true,
  isGrayedOut = false,
  isEditActive = false,
  userAnswer,
  answerOptions,
  options,
  onDescriptionUpdate,
  onReasoningUpdate,
  onUserAnswerUpdate,
  onOptionSelect,
  onHeightChange,
  onEditStart,
  onEditEnd
}: QuestionaireComponentProps) => {
  const [activeOption, setActiveOption] = useState<string | null>(null);
  const [focusedOption, setFocusedOption] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!onHeightChange || !containerRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      onHeightChange(entry.contentRect.height);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [onHeightChange]);

  const showDescriptionAndReasoning =
    description !== undefined || reasoning !== undefined;

  const iconFull = (
    <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-[linear-gradient(90deg,_#F0F4FD_0.83%,_#ADBDF4_100%)]">
      {icon}
    </div>
  );

  if (isLoading) return <QuestionaireComponentSkeleton icon={iconFull} />;

  return (
    <div
      ref={containerRef}
      className={`flex gap-4 p-4 rounded-lg border border-gray-100 text-gray-900v2 text-left ${isDisabled ? 'opacity-[0.38] cursor-not-allowed' : ''}`}
    >
      <div
        className="relative z-[1] flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full"
        style={{
          background: isGrayedOut
            ? 'linear-gradient(90deg, #F7F7F8 0.83%, #BABBBF 100%)'
            : 'linear-gradient(90deg, #F0F4FD 0.83%, #ADBDF4 100%)'
        }}
      >
        {icon}
      </div>

      <div className="flex flex-col gap-1 w-full">
        <span className="text-base font-normal text-[#27272A]">{mainText}</span>

        {showDescriptionAndReasoning && (
          <DescriptionAndReasoning
            title={descriptionTitle || reasoningTitle}
            content={description || reasoning || ''}
            isDescription={description !== undefined}
            isEditable={isEditable}
            isDisabled={isDisabled}
            onUpdate={
              description !== undefined
                ? onDescriptionUpdate
                : onReasoningUpdate
            }
            onEditStart={onEditStart}
            onEditEnd={onEditEnd}
          />
        )}

        {userAnswer && (
          <UserAnswer
            userAnswer={userAnswer}
            isEditable={isEditable}
            isDisabled={isDisabled}
            isEditActive={isEditActive}
            options={answerOptions}
            onUpdate={onUserAnswerUpdate}
            onEditStart={onEditStart}
            onEditEnd={onEditEnd}
          />
        )}

        {options && (
          <div className="flex flex-wrap gap-2 pt-1">
            {options.map((option) => {
              const isActive = activeOption === option;
              return (
                <button
                  key={option}
                  className={`py-[17px] px-[24px] h-[54px] flex items-center rounded-lg border transition-colors focus:outline-none font-medium
                    ${
                      isActive
                        ? 'ring-2 ring-purple-400 bg-blue-50 text-purple-900'
                        : 'border ring-gray-300 hover:ring-gray-400 text-gray-900'
                    }`}
                  style={{
                    boxShadow:
                      focusedOption === option ? OPTION_FOCUS_SHADOW : undefined
                  }}
                  onClick={() => {
                    if (isDisabled) return;
                    setActiveOption(option);
                    onOptionSelect?.(option);
                    setTimeout(() => {
                      setActiveOption(null);
                      setFocusedOption(null);
                    }, 200);
                  }}
                  disabled={isDisabled}
                  onFocus={() => setFocusedOption(option)}
                  onBlur={() => setFocusedOption(null)}
                >
                  <span className="text-sm">{option}</span>
                </button>
              );
            })}
          </div>
        )}

        {isError && (
          <>
            <p className="text-sm text-gray-900 mb-1 mt-[-4px]">
              {errorMessage ||
                "I couldn't generate your next question just now. Let's try that again."}
            </p>
            <PrimaryButton
              size="xs"
              className="w-fit"
              onClick={() => alert('error')}
            >
              Try Again
            </PrimaryButton>
          </>
        )}
      </div>
    </div>
  );
};
